import { useContext } from 'react';

import config from 'config';
import { trustedUsLimitMap } from 'consts/trustedUs';
import withMetatags from 'core/hocs/withMetatags';

import { ConfigContext } from '@providers/ConfigProvider';
import TrustedUs from '@components/TrustedUs';
import HeroSection from '@components/molecules/HeroSection';
import StaticPageFirstSection, { firstSectionItems } from '@components/StaticPageFirstSection';

import ExpertSection from './components/ExpertSection';
import ContactUs from './components/ContactUs';
import DialogsBoxes from './components/DialogsBoxes';
import Proposals from './components/Proposals';
import ContactUsImage from './components/ContactUsImage';
import ProsAndCons from './components/ProsAndCons';
import WhyTrustUs from './components/WhyTrustUs';

import Props from './PublicSector.types';
import classes from './PublicSector.module.scss';

const { routes } = config;

const routesMap: Record<Props['variant'], string> = {
  publicSector: routes.publicSector.href,
  complexDelivery: routes.complexDelivery.href,
};

const PublicSector = ({
  variant,
  heroSection,
  firstSection,
  secondSection,
  dialogBoxesSection,
  prosAndConsSection,
  contactUsImageSection,
  numbers,
  trustedUsCount,
}: Props): JSX.Element => {
  const { isBiznesowe, isPremiumStation } = useContext(ConfigContext);

  return (
    <div className={classes.wrapper}>
      {heroSection && <HeroSection title={heroSection.title} subtitle={heroSection.subtitle} />}
      <div className={classes.container}>
        {!isPremiumStation && <StaticPageFirstSection {...firstSection} items={firstSectionItems} />}
        {secondSection && (
          <ExpertSection
            className={isPremiumStation ? classes.premiumStationExpertSection : classes.expertSection}
            title={secondSection.title}
            titleDescription={secondSection.titleDescription}
            description={secondSection.description}
            itemsPerRow={secondSection.itemsPerRow}
            items={secondSection.items}
          />
        )}
        <ContactUs />
        {dialogBoxesSection && (
          <DialogsBoxes
            firstSection={dialogBoxesSection.firstSection}
            secondSection={dialogBoxesSection.secondSection}
            className={classes.dialogBoxesWrapper}
          />
        )}
        <Proposals variant={variant} />
        {!!trustedUsCount?.aggregate.count && (
          <TrustedUs page={routesMap[variant]} limit={trustedUsLimitMap[routesMap[variant]]} />
        )}
        {contactUsImageSection && (
          <ContactUsImage
            className={classes.contactUsSection}
            buttonText={contactUsImageSection.buttonText}
            title={contactUsImageSection.title}
            items={contactUsImageSection.items}
          />
        )}
        {isBiznesowe && prosAndConsSection && (
          <ProsAndCons
            className={classes.prosAndCons}
            title={prosAndConsSection.title}
            descriptionFirstPart={prosAndConsSection.descriptionFirstPart}
            descriptionSecondPart={prosAndConsSection.descriptionSecondPart}
            consSection={prosAndConsSection.consSection}
            firstPros={prosAndConsSection.firstPros}
            secondPros={prosAndConsSection.secondPros}
            numbersSection={prosAndConsSection.numbersSection}
          />
        )}
        <WhyTrustUs numbers={numbers} />
        <ContactUs className={classes.lastContactUs} />
      </div>
    </div>
  );
};

export default withMetatags(PublicSector, { withCanonicalUrl: true });
